import { resolveSaleFinance } from './financeCostEngine';
import { DEFAULT_FINANCE_COST_SETTINGS } from './financeCostConstants';
import { allocateMonthlyAmount, islemToDate } from './financePeriod';
import { toFiniteNumber } from './financeGoldRate';

function round2(n) {
  if (n == null || !Number.isFinite(Number(n))) return 0;
  return Math.round(Number(n) * 100) / 100;
}

export function isSkippedFinanceIslem(islem) {
  if (islem?.hizliIslemTipi === 'bankaya_nakit_cik' || islem?.hizliIslemTipi === 'bankadan_kasa_nakit') {
    return true;
  }
  const urun = String(islem?.urun || '');
  return urun.includes('Bakiye Düzeltme') || urun.includes('Manuel Borç') || urun.includes('Manuel Alacak');
}

export function isActiveExpense(row) {
  if (!row) return false;
  const status = String(row.status || 'active').toLowerCase();
  return status !== 'cancelled' && status !== 'canceled' && status !== 'iptal';
}

export function expenseAmount(row) {
  if (row?.amountTry != null && row.amountTry !== '') return toFiniteNumber(row.amountTry);
  return toFiniteNumber(row?.tutar);
}

export function expenseDateKey(row) {
  return String(row?.expenseDate || row?.tarih || '');
}

export function percentChange(current, previous) {
  if (previous == null || !Number.isFinite(Number(previous)) || Number(previous) === 0) return null;
  return round2(((Number(current) - Number(previous)) / Math.abs(Number(previous))) * 100);
}

export function periodExpenseAmount(row, range) {
  const monthly = expenseAmount(row);
  return allocateMonthlyAmount(monthly, range?.startYmd, range?.endYmd);
}

/** Kredi karti tahsilatindan bankanin kestigi komisyon (satis basina). */
export function posCommissionFor(islem, settings) {
  const pct = toFiniteNumber(
    settings?.posCommissionPct != null ? settings.posCommissionPct : DEFAULT_FINANCE_COST_SETTINGS.posCommissionPct
  );
  if (!(pct > 0)) return 0;
  const kart = toFiniteNumber(islem?.krediKarti);
  if (!(kart > 0)) return 0;
  return round2((kart * pct) / 100);
}

export function summarizeFinancePeriod({ islemler = [], giderler = [], prices, range, settings } = {}) {
  let revenueTry = 0;
  let posCommissionTry = 0;
  let cogsTry = 0;
  let saleProfitTry = 0;
  let purchaseProfitTry = 0;
  let salesCount = 0;
  let purchaseCount = 0;
  let costCompleteSalesCount = 0;
  let costMissingSalesCount = 0;
  const sales = [];

  for (const islem of islemler) {
    if (isSkippedFinanceIslem(islem)) continue;
    const tur = String(islem?.islemTuru || '');
    const fin = resolveSaleFinance(islem, { prices, settings });

    if (tur === 'Alış') {
      purchaseCount += 1;
      if (fin.ok) {
        purchaseProfitTry = round2(purchaseProfitTry + (fin.grossProfitTry || 0));
      }
      sales.push({ islem, fin, sign: 1, kind: 'alis', date: islemToDate(islem) });
      continue;
    }

    if (tur !== 'Satış' && tur !== 'İade') continue;

    const sign = tur === 'İade' ? -1 : 1;
    const sale = fin.saleAmountTry != null ? fin.saleAmountTry : toFiniteNumber(islem.toplamTutar);
    revenueTry = round2(revenueTry + sign * (sale || 0));
    // Kredi karti komisyonu kar degil, bankaya giden gider.
    const komisyon = posCommissionFor(islem, settings);
    posCommissionTry = round2(posCommissionTry + sign * komisyon);
    salesCount += 1;
    if (fin.ok) {
      cogsTry = round2(cogsTry + sign * (fin.totalCostTry || 0));
      saleProfitTry = round2(saleProfitTry + sign * (fin.grossProfitTry || 0));
      costCompleteSalesCount += 1;
    } else {
      costMissingSalesCount += 1;
    }
    sales.push({
      islem,
      fin,
      sign,
      kind: tur === 'İade' ? 'iade' : 'satis',
      date: islemToDate(islem),
      posCommissionTry: komisyon
    });
  }

  const enteredExpenseTry = round2(
    giderler.filter(isActiveExpense).reduce((sum, row) => sum + periodExpenseAmount(row, range), 0)
  );
  // POS komisyonu girilen giderlere eklenir: Gider kartı, gider dağılımı ve
  // net kâr hepsi aynı toplamı gösterir.
  const expenseTry = round2(enteredExpenseTry + posCommissionTry);
  const grossProfitTry = round2(saleProfitTry + purchaseProfitTry);
  const netProfitTry = round2(grossProfitTry - expenseTry);
  const netMarginPct = revenueTry === 0 ? null : round2((netProfitTry / revenueTry) * 100);
  const denom = salesCount;
  const costCoveragePct = denom === 0 ? 100 : round2((costCompleteSalesCount / denom) * 100);

  return {
    revenueTry,
    cogsTry,
    grossProfitTry,
    saleProfitTry,
    purchaseProfitTry,
    expenseTry,
    enteredExpenseTry,
    posCommissionTry,
    netProfitTry,
    netMarginPct,
    salesCount,
    purchaseCount,
    costCompleteSalesCount,
    costMissingSalesCount,
    costCoveragePct,
    sales,
    ciro: revenueTry,
    maliyet: cogsTry,
    brutKar: grossProfitTry,
    gider: expenseTry,
    net: netProfitTry,
    missing: costMissingSalesCount,
    satisAdet: salesCount
  };
}

export function compareFinanceSummaries(current, previous) {
  return {
    revenueTry: percentChange(current?.revenueTry, previous?.revenueTry),
    cogsTry: percentChange(current?.cogsTry, previous?.cogsTry),
    grossProfitTry: percentChange(current?.grossProfitTry, previous?.grossProfitTry),
    expenseTry: percentChange(current?.expenseTry, previous?.expenseTry),
    netProfitTry: percentChange(current?.netProfitTry, previous?.netProfitTry),
    netMarginPct: percentChange(current?.netMarginPct, previous?.netMarginPct)
  };
}

export function expenseBreakdown(giderler = [], range) {
  const map = {};
  for (const row of giderler.filter(isActiveExpense)) {
    const key = row.category || row.kalem || 'diger';
    if (!map[key]) map[key] = { category: key, amountTry: 0, monthlyTry: 0 };
    const monthly = expenseAmount(row);
    const amt = periodExpenseAmount(row, range);
    map[key].monthlyTry = round2(map[key].monthlyTry + monthly);
    map[key].amountTry = round2(map[key].amountTry + amt);
  }
  return Object.values(map).sort((a, b) => b.amountTry - a.amountTry);
}

/**
 * Gider dagilimi. POS komisyonu gider kaydi degil, satislardan hesaplanir;
 * `posCommissionTry` verilirse listeye ayri satir olarak eklenir.
 */
export function expenseItemBreakdown(giderler = [], range, posCommissionTry = 0) {
  const rows = giderler
    .filter(isActiveExpense)
    .map((row, idx) => ({
      id: row.id || `${row.title || row.category || 'gider'}-${idx}`,
      title: String(row.title || row.description || row.category || row.kalem || 'Gider'),
      category: row.category || row.kalem || 'diger',
      amountTry: periodExpenseAmount(row, range),
      monthlyTry: expenseAmount(row)
    }))
    .filter((row) => row.amountTry > 0);
  const komisyon = toFiniteNumber(posCommissionTry);
  if (komisyon > 0) {
    rows.push({
      id: 'pos-komisyonu',
      title: 'POS komisyonu',
      category: 'pos_komisyonu',
      amountTry: round2(komisyon),
      monthlyTry: null,
      auto: true
    });
  }
  return rows.sort((a, b) => b.amountTry - a.amountTry || String(a.title).localeCompare(String(b.title), 'tr'));
}
